import { z } from 'zod';

/**
 * Editable robot (installation) metadata — the client mirror of the
 * `GET /api/installations/:id` and `PATCH /api/installations/:id` handlers
 * (`rust/server/src/routes/installations.rs`). An "installation" is one seat of a
 * licence (`activations.id`). Every editable field is nullable — an empty value is
 * `null`, never an empty string.
 */
export const installationMetadataSchema = z.object({
  id: z.string(),
  licenseId: z.string(),
  installationId: z.string().nullable(),
  fingerprintHash: z.string(),
  activatedAt: z.string(),
  lastSeen: z.string(),
  deactivatedAt: z.string().nullable(),
  label: z.string().nullable(),
  notes: z.string().nullable(),
  location: z.string().nullable(),
});

/** The partial-update body of `PATCH /api/installations/:id` (whitelist). */
export const installationMetadataPatchSchema = z.object({
  label: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
});

/**
 * One row of `GET /api/installations?license_id=<id>` — the editable facts of a
 * single seat, keyed by its activation id. Seats without a saved row are absent
 * from the list.
 */
export const installationMetadataListItemSchema = z.object({
  activationId: z.string(),
  label: z.string().nullable(),
  notes: z.string().nullable(),
  location: z.string().nullable(),
  updatedAt: z.string(),
});

/** The `{ data: [...] }` envelope of the licence-scoped metadata list. */
export const installationMetadataListSchema = z.object({
  data: z.array(installationMetadataListItemSchema),
});

export type InstallationMetadata = z.infer<typeof installationMetadataSchema>;
export type InstallationMetadataPatch = z.infer<typeof installationMetadataPatchSchema>;
export type InstallationMetadataListItem = z.infer<typeof installationMetadataListItemSchema>;
